import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faEnvelope, faUser, faPhone, faIdCard } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';

interface AddTeamMemberModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  brokerageInfo?: {
    id: string;
    name: string;
  };
}

const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
  show,
  onHide,
  onSuccess,
  brokerageInfo
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    licenseNumber: '',
    role: 'agent',
    sendInvite: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // First, check if user already exists
      const checkResponse = await axiosInstance.post('/broker/invitations/check-existing', {
        email: formData.email
      });

      if (checkResponse.data.userExists && checkResponse.data.inSameBrokerage) {
        setError('This user is already a member of your brokerage.');
        setLoading(false);
        return;
      }

      let response;
      
      if (checkResponse.data.userExists && !checkResponse.data.inSameBrokerage) {
        // User exists but not in this brokerage - link them
        response = await axiosInstance.post('/broker/invitations/link-existing', {
          email: formData.email,
          brokerage_id: brokerageInfo?.id,
          role: formData.role
        });
        
        toast.success('User added to your brokerage successfully!');
      } else {
        // New user - send invitation
        if (formData.sendInvite) {
          response = await axiosInstance.post('/broker/invitations/send-simple', {
            agentData: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              licenseNumber: formData.licenseNumber
            }
          });
          
          toast.success('Invitation sent successfully!');
        } else {
          // Save without sending invite
          response = await axiosInstance.post('/broker/save-agent', {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            licenseNumber: formData.licenseNumber,
            role: formData.role
          });
          
          toast.success('Team member added successfully!');
        }
      }

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        licenseNumber: '',
        role: 'agent',
        sendInvite: true
      });
      
      onSuccess();
      onHide();
    } catch (err: any) {
      console.error('Error adding team member:', err);
      setError(err.response?.data?.error || 'Failed to add team member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faUserPlus} className="me-2" />
          Add Team Member
          {brokerageInfo && (
            <small className="text-muted ms-2">to {brokerageInfo.name}</small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <div className="mb-4">
            <h6 className="text-muted mb-3">Team Member Information</h6>
            
            <Form.Group className="mb-3">
              <Form.Label>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Email Address <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="agent@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                If they already have a KairosIQ account, they'll be added directly to your brokerage.
              </Form.Text>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    First Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    Phone Number
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FontAwesomeIcon icon={faIdCard} className="me-2" />
                    License Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="BR123456"
                    value={formData.licenseNumber}
                    onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Role in Brokerage</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="agent">Agent</option>
                <option value="broker">Broker</option>
                <option value="admin">Admin</option>
                <option value="assistant">Assistant</option>
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Check
                type="checkbox"
                id="sendInvite"
                label="Send email invitation to join KairosIQ"
                checked={formData.sendInvite}
                onChange={(e) => setFormData({ ...formData, sendInvite: e.target.checked })}
              />
              <Form.Text className="text-muted">
                If unchecked, the team member will be added but won't receive an invitation email.
              </Form.Text>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || !formData.email || !formData.firstName || !formData.lastName}
          >
            {loading ? 'Adding...' : formData.sendInvite ? 'Send Invitation' : 'Add Team Member'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddTeamMemberModal;